import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnDestroy } from '@angular/core';

import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ContentLink } from '@frontend/vanilla/core';
import { BehaviorSubject, noop } from 'rxjs';

import { BetBuilderButtonSource } from '../../bet-builder/model/bet-builder.model';
import { TopEventsMappingService } from '../top-events-mapping.service';
import { TopEventsTrackingService } from '../top-events-tracking.service';
import { EventDataForOptionPanel, PodType, TopEventsPodData } from '../top-events.model';

@Component({
    selector: 'ms-top-events-betbuilder',
    templateUrl: 'top-events-betbuilder.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TopEventsBetBuilderComponent implements OnChanges, AfterViewChecked, OnDestroy {
    @HostBinding('class') className = 'betbuilder-pod';
    @HostBinding('class.single-event-bab') get singleEvent() {
        return this.pod?.events?.length === 1;
    }

    @HostBinding('class.pod-header-hidden') get podHeaderHidden() {
        return this.pod?.hidePodHeader;
    }

    @HostBinding('class.hide-bab-cta') get hideBetBuilderButton() {
        return !!this.pod?.hideBetBuilderButton;
    }

    @Input() pod: TopEventsPodData;
    @Input() index: number;

    vms$ = new BehaviorSubject<EventDataForOptionPanel[]>([]);
    link?: ContentLink;
    limit: number;

    babButtonSource = BetBuilderButtonSource.TopEvents;
    babEpcotEnabled = false;

    tracking = `TopEvents|${PodType.BetBuilder}`;
    trackingModuleType = PodType.BetBuilder;
    contentRendered = false;

    constructor(
        public sitecore: Sitecore,
        private topEventsPodService: TopEventsMappingService,
        private changeDetectionRef: ChangeDetectorRef,
        private topEventsTrackingService: TopEventsTrackingService,
        epcotConfig: EpcotConfig,
    ) {
        this.babEpcotEnabled = epcotConfig.isEnabled;
    }

    showHideEvents(isExpanded: boolean): void {
        this.topEventsTrackingService.trackShowMoreLess(this.pod, this.index, isExpanded);

        this.limit = this.limit < this.vms$.value.length ? this.vms$.value.length : (this.pod.eventsBeforeShowMore ?? this.vms$.value.length);
        this.changeDetectionRef.detectChanges();
    }

    trackByEventId(index: number, item: EventDataForOptionPanel): string {
        return item.eventModel.id;
    }

    ngAfterViewChecked(): void {
        this.contentRendered = true;
    }

    ngOnChanges(changes: ISimpleChanges<TopEventsBetBuilderComponent>): void {
        this.pod.updateHandler = this.updateViewModel;
        if (changes.pod) {
            this.updateViewModel();
        }
    }

    private updateViewModel = (): void => {
        const vms = this.pod.events.map((e) => {
            if (this.pod.betBuilderOptionIds && this.pod.betBuilderOptionIds.length) {
                return this.topEventsPodService.getPodEventDataBetBuilderOptions(
                    {
                        eventData: e,
                        sourceFixtures: this.pod.sourceFixtures,
                        selectionTextOverride: undefined,
                        isDetailed: true,
                    },
                    this.pod.betBuilderOptionIds,
                );
            }

            return this.topEventsPodService.getPodEventData({
                eventData: e,
                sourceFixtures: this.pod.sourceFixtures,
                selectionTextOverride: undefined,
                isDetailed: true,
            });
        });

        this.vms$.next(vms);
        this.link = this.pod.link;
        this.limit = this.pod.eventsBeforeShowMore ?? vms.length;
    };

    trackBottomLink(): void {
        this.topEventsTrackingService.trackBottomLink(this.pod, this.index);
    }

    ngOnDestroy(): void {
        this.pod.updateHandler = noop;
    }
}
